import React, { useState, useCallback, useRef, ReactNode } from 'react';

import { EDI_FILE_ACCEPT_EXTENSIONS, isEdiFile } from '../../services/fileParser';

type DragStatus = 'idle' | 'valid' | 'invalid';

interface DropZoneWrapperProps {
    children: ReactNode;
    onFilesDropped: (files: File[]) => void;
    acceptTypes?: string; // e.g., ".pdf,.doc,.docx,image/*"
    className?: string;
    overlayClassName?: string;
    id?: string;
    disabled?: boolean;
}

const EXTENSION_TO_MIME: Record<string, string> = {
    '.json': 'application/json',
    '.txt': 'text/plain',
    '.pdf': 'application/pdf',
};

const EDI_EXTENSION_ACCEPTS = new Set(
    EDI_FILE_ACCEPT_EXTENSIONS.map((ext) => ext.toLowerCase()),
);

const GENERIC_BINARY_MIME_TYPES = new Set([
    '',
    'application/octet-stream',
    'application/edi-x12',
    'text/plain',
]);

function acceptListAllowsEdi(acceptList: string[]): boolean {
    return acceptList.some(
        (accept) => accept.startsWith('.') && EDI_EXTENSION_ACCEPTS.has(accept.toLowerCase()),
    );
}

export const ZIP_UNSUPPORTED_MESSAGE =
    'ZIP files are not supported. Extract the archive and upload the supported files individually.';

export function isZipFileName(name: string): boolean {
    const dot = name.lastIndexOf('.');
    if (dot < 0) return false;
    return name.slice(dot + 1).toLowerCase() === 'zip';
}

export function attachmentRejectionMessage(fileNames: string[]): string {
    if (fileNames.length > 0 && fileNames.every(isZipFileName)) {
        return ZIP_UNSUPPORTED_MESSAGE;
    }
    if (fileNames.length === 1) return `${fileNames[0]} is not supported.`;
    if (fileNames.length > 1) return `${fileNames.join(', ')} are not supported.`;
    return 'This file type is not supported.';
}

export function isFileAccepted(file: { name: string; type: string }, acceptList: string[]): boolean {
    // A .zip is an archive even when the browser reports an allowed MIME type.
    if (isZipFileName(file.name)) return false;

    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    const extensionMatch = acceptList.some((accept) => {
        if (accept.startsWith('.')) {
            return fileName.endsWith(accept.toLowerCase());
        }
        if (accept.includes('/')) {
            return fileType === accept.toLowerCase() || fileType.startsWith(`${accept.toLowerCase()}/`);
        }
        return false;
    });
    if (extensionMatch) return true;

    // EDI files are often reported as application/octet-stream with only the
    // extension to identify them.
    return acceptListAllowsEdi(acceptList) && isEdiFile(file.name);
}

function dragItemLooksAccepted(
    item: DataTransferItem,
    extensionAccepts: string[],
    acceptedMimes: Set<string>,
): boolean {
    const type = (item.type || '').toLowerCase();
    if (type && acceptedMimes.has(type)) return true;

    // Browsers usually expose EDI as octet-stream (or no type) during drag.
    // Defer to the filename/extension check on drop instead of blocking here.
    if (extensionAccepts.length > 0 && GENERIC_BINARY_MIME_TYPES.has(type)) {
        return true;
    }

    return false;
}

function formatAcceptTypes(acceptTypes: string): string {
    return acceptTypes
        .split(',')
        .map(t => t.trim())
        .filter(t => t.startsWith('.'))
        .map(t => t.toUpperCase().replace('.', ''))
        .join(', ');
}

const REJECTION_DISPLAY_MS = 3000;

const DropZoneWrapper: React.FC<DropZoneWrapperProps> = ({
    children,
    onFilesDropped,
    acceptTypes,
    className = '',
    overlayClassName = '',
    id,
    disabled = false,
}) => {
    const [dragStatus, setDragStatus] = useState<DragStatus>('idle');
    const [rejectedNames, setRejectedNames] = useState<string[]>([]);
    const rejectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const parsedAcceptList = acceptTypes?.split(',').map(t => t.trim());

    const clearRejection = useCallback(() => {
        if (rejectionTimerRef.current) clearTimeout(rejectionTimerRef.current);
        rejectionTimerRef.current = null;
        setDragStatus('idle');
        setRejectedNames([]);
    }, []);

    const classifyDragItems = useCallback((dataTransfer: DataTransfer): DragStatus => {
        if (!parsedAcceptList) return 'valid';

        const items = Array.from(dataTransfer.items);
        const fileItems = items.filter(item => item.kind === 'file');
        if (fileItems.length === 0) return 'valid';

        const extensionAccepts = parsedAcceptList.filter((accept) => accept.startsWith('.'));
        const acceptedMimes = new Set<string>();
        parsedAcceptList.forEach((accept) => {
            if (accept.includes('/')) {
                acceptedMimes.add(accept.toLowerCase());
            } else if (accept.startsWith('.') && EXTENSION_TO_MIME[accept.toLowerCase()]) {
                acceptedMimes.add(EXTENSION_TO_MIME[accept.toLowerCase()]);
            }
        });

        const hasAnyValid = fileItems.some((item) =>
            dragItemLooksAccepted(item, extensionAccepts, acceptedMimes),
        );

        return hasAnyValid ? 'valid' : 'invalid';
    }, [parsedAcceptList]);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('Files')) {
            clearRejection();
            const status = classifyDragItems(e.dataTransfer);
            setDragStatus(status);
            e.dataTransfer.dropEffect = status === 'invalid' ? 'none' : 'copy';
        }
    }, [disabled, classifyDragItems, clearRejection]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('Files')) {
            const status = classifyDragItems(e.dataTransfer);
            setDragStatus(status);
            e.dataTransfer.dropEffect = status === 'invalid' ? 'none' : 'copy';
        }
    }, [disabled, classifyDragItems]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        const currentTarget = e.currentTarget as HTMLElement;
        const relatedTarget = e.relatedTarget as Node | null;
        // relatedTarget is null when the drag leaves the document entirely (e.g. dragged
        // out through the top of the browser into the tab/address bar), where clientX/Y
        // get clamped to viewport bounds instead of going negative, making a coordinate
        // check unreliable. It's a descendant of currentTarget when we just moved onto a
        // child element, which should not clear the overlay.
        if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
            setDragStatus('idle');
        }
    }, [disabled]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();

        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length === 0) {
            setDragStatus('idle');
            return;
        }

        let validFiles = droppedFiles;
        let invalidFiles: File[] = [];

        if (parsedAcceptList) {
            validFiles = droppedFiles.filter(f => isFileAccepted(f, parsedAcceptList));
            invalidFiles = droppedFiles.filter(f => !isFileAccepted(f, parsedAcceptList));
        }

        if (validFiles.length > 0) {
            onFilesDropped(validFiles);
        }

        if (invalidFiles.length > 0) {
            setRejectedNames(invalidFiles.map(f => f.name));
            setDragStatus('invalid');
            rejectionTimerRef.current = setTimeout(clearRejection, REJECTION_DISPLAY_MS);
        } else {
            setDragStatus('idle');
        }
    }, [parsedAcceptList, onFilesDropped, disabled, clearRejection]);

    const isDragging = dragStatus === 'valid' || dragStatus === 'invalid';
    const isShowingRejection = dragStatus === 'invalid' && rejectedNames.length > 0;
    const showOverlay = (isDragging || isShowingRejection) && !disabled;

    return (
        <section 
            id={id}
            className={`relative ${className}`}
            onDragEnter={disabled ? undefined : handleDragEnter}
            onDragOver={disabled ? undefined : handleDragOver}
            onDragLeave={disabled ? undefined : handleDragLeave}
            onDrop={disabled ? undefined : handleDrop}
        >
            {showOverlay && (
                <div className={`absolute inset-0 z-50 flex items-center justify-center backdrop-blur-xs border-4 border-dashed rounded-lg m-2 transition-colors duration-200 ${
                    dragStatus === 'invalid'
                        ? 'bg-red-500/10 border-red-400'
                        : 'bg-blue-500/10 border-blue-400'
                } ${overlayClassName}`}>
                    <div className={`flex flex-col items-center gap-3 px-8 py-6 bg-white rounded-xl shadow-lg border transition-colors duration-200 ${
                        dragStatus === 'invalid' ? 'border-red-200' : 'border-blue-200'
                    }`}>
                        {dragStatus === 'invalid' ? (
                            <>
                                <svg
                                    className="w-16 h-16 text-red-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                    />
                                </svg>
                                <div className="text-center">
                                    <p className="text-lg font-semibold text-red-700 font-manrope">
                                        Unsupported file type
                                    </p>
                                    <p className="text-sm text-red-500 mt-1 font-manrope max-w-xs">
                                        {attachmentRejectionMessage(rejectedNames)}
                                    </p>
                                    {acceptTypes && (
                                        <p className="text-xs text-gray-500 mt-2 font-manrope">
                                            Accepted formats: {formatAcceptTypes(acceptTypes)}
                                        </p>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <svg 
                                    className="w-16 h-16 text-blue-500" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={1.5} 
                                        d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15m0-3l-3-3m0 0l-3 3m3-3V15" 
                                    />
                                </svg>
                                <div className="text-center">
                                    <p className="text-lg font-semibold text-gray-800 font-manrope">
                                        Drop files here to attach
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1 font-manrope">
                                        Release to add files to your message
                                    </p>
                                    {acceptTypes && (
                                        <p className="text-xs text-gray-500 mt-2 font-manrope">
                                            Supports {formatAcceptTypes(acceptTypes)}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {children}
        </section>
    );
};

export default DropZoneWrapper;
